/**
 * The one visual language for quiz answers, shared by every question type
 * (multiple choice, multiple select, true/false, fill in the blank). Each type
 * used to carry its own copy of these classes in raw palette colours, and the
 * copies had drifted: the explanation box went blue on a wrong answer, and a
 * correct option the learner missed looked identical to one they picked.
 *
 * Styled like the marketing site's quiz card: a thin border and a soft tint,
 * with the text left in the normal ink colour rather than turned green or red,
 * so the answer stays as readable as the question. Tokens throughout, so dark
 * mode needs no per-state overrides.
 */
export type AnswerState =
  /** Before submit, not chosen. */
  | 'idle'
  /** Before submit, chosen. */
  | 'selected'
  /** After submit: a right answer the learner chose (or the right option). */
  | 'correct'
  /** After submit: a wrong answer the learner chose. */
  | 'wrong'
  /** After submit, multiple select only: a right option the learner left out. */
  | 'missed'
  /** After submit: an option that was neither chosen nor right. */
  | 'neutral';

export const ANSWER_BASE =
  'w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors duration-200 sm:p-4';

export const ANSWER_TONE: Record<AnswerState, string> = {
  idle: 'border-border text-foreground hover:border-brand-300 hover:bg-accent/50 dark:hover:border-primary/50',
  // Unmistakable on a phone: a filled tint and a full-strength ring, not a
  // thin border change that was hard to see at arm's length.
  selected:
    'border-brand-500 bg-brand-50 text-foreground ring-1 ring-brand-500 dark:border-primary dark:bg-primary/15 dark:ring-primary',
  correct: 'border-practice/50 bg-practice/10 text-foreground',
  wrong: 'border-destructive/50 bg-destructive/10 text-foreground',
  missed: 'border-dashed border-practice/60 text-foreground',
  neutral: 'border-border text-muted-foreground',
};

/**
 * Where an option or typed answer stands. `picked` is whether the learner
 * chose it; `right` whether it is (one of) the correct answer(s).
 */
export function answerState({
  revealed,
  picked,
  right,
  multiple = false,
}: {
  revealed: boolean;
  picked: boolean;
  right: boolean;
  multiple?: boolean;
}): AnswerState {
  if (!revealed) return picked ? 'selected' : 'idle';
  if (picked) return right ? 'correct' : 'wrong';
  if (right) return multiple ? 'missed' : 'correct';
  return 'neutral';
}

/** The A / B / C / D marker's fill per state - it carries the choice too. */
export const MARKER_TONE: Record<AnswerState, string> = {
  idle: 'border-border text-muted-foreground',
  selected:
    'border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary dark:text-primary-foreground',
  correct: 'border-practice bg-practice text-white',
  wrong: 'border-destructive bg-destructive text-white',
  missed: 'border-dashed border-practice text-practice-emphasis',
  neutral: 'border-border text-muted-foreground',
};
